/**
 * Centralized Guardian Configuration
 * Single source of truth for all guardian data across the application
 */

// Import all guardian images
import steriImg from '@/assets/images/patty/guardian/steri.svg';
import handHygieneImg from '@/assets/images/patty/guardian/hand-hygenie.svg';
import environmentImg from '@/assets/images/patty/guardian/environment.svg';
import healthSafetyImg from '@/assets/images/patty/guardian/health-safety.svg';
import constructionImg from '@/assets/images/patty/guardian/construction.svg';
import dataAnalysisImg from '@/assets/images/patty/guardian/data-analysis.svg';

/**
 * Guardian definitions with all properties
 * @property {string} id - Unique identifier
 * @property {string} name - Guardian's name
 * @property {string} label - Display label/expertise area
 * @property {string} agentType - Backend agent type
 * @property {*} image - Imported image asset
 * @property {string} imagePath - Relative path to image
 * @property {string} description - Short description for thread view
 * @property {string} shortDescription - Truncated description for cards
 * @property {string} fullDescription - Full detailed description
 * @property {string[]} keywords - Keywords for suggestion matching
 */
export const GUARDIANS = [
    {
        id: 'data-analysis',
        name: 'Quincy',
        label: 'Data Analysis',
        agentType: 'DATA_ANALYSIS',
        image: dataAnalysisImg,
        imagePath: 'patty/guardian/data-analysis.svg',
        description: 'I can assist with any questions you have about IPAC.',
        shortDescription:
            'Supports IPAC, health and safety, and compliance-related documentation and record management.',
        fullDescription:
            'Supports IPAC, health and safety, and compliance-related documentation and record management. This Guardian assists with required logs, audits, inspection readiness, record retention, and organizing evidence to support regulatory compliance and operational consistency.',
        keywords: ['data', 'analysis', 'data analysis'],
    },
    {
        id: 'hand-hygiene',
        name: 'Finn',
        label: 'Hand Hygiene',
        agentType: 'HAND_HYGIENE',
        image: handHygieneImg,
        imagePath: 'patty/guardian/hand-hygenie.svg',
        description:
            'I can assist with any questions you have about Hand Hygiene.',
        shortDescription:
            'Supports best-practice hand hygiene programs with clear, actionable guidance.',
        fullDescription:
            'Supports best-practice hand hygiene programs with clear, actionable guidance. Finn provides instruction on proper technique, hand sanitizer use, glove protocols, and compliance monitoring to strengthen hand hygiene practices across the organization.',
        keywords: [
            'hand hygiene',
            'handwash',
            'hand wash',
            'sanitizer',
            'glove',
        ],
    },
    {
        id: 'environmental',
        name: 'Max',
        label: 'Environmental',
        agentType: 'ENVIRONMENTAL',
        image: environmentImg,
        imagePath: 'patty/guardian/environment.svg',
        description:
            'I can assist with any questions you have about Environmental cleaning and disinfection.',
        shortDescription:
            'Offers detailed guidance on environmental cleaning and disinfection best practices.',
        fullDescription:
            'Offers detailed guidance on environmental cleaning and disinfection best practices. Max supports surface disinfection protocols, cleaning frequencies, product selection, and environmental monitoring aligned with IPAC and regulatory expectations for safe care environments.',
        keywords: ['environment', 'cleaning', 'surface'],
    },
    {
        id: 'sterilization',
        name: 'Juno',
        label: 'Sterilization',
        agentType: 'STERILIZATION',
        image: steriImg,
        imagePath: 'patty/guardian/steri.svg',
        description:
            'I can assist with any questions you have about Sterilization.',
        shortDescription:
            'Provides expert support for sterilization and reprocessing workflows.',
        fullDescription:
            'Provides expert support for sterilization and reprocessing workflows. Juno offers guidance on sterilization techniques, equipment maintenance, monitoring requirements, and applicable regulatory standards across steam, chemical, and low-temperature sterilization methods.',
        keywords: ['steril', 'autoclave', 'instrument', 'disinfection'],
    },
    {
        id: 'health-safety',
        name: 'Astra',
        label: 'Health & Safety',
        agentType: 'HEALTH_SAFETY',
        image: healthSafetyImg,
        imagePath: 'patty/guardian/health-safety.svg',
        description:
            'I can assist with any questions you have about Health & Safety.',
        shortDescription:
            'Helps reinforce workplace health and safety, PPE use, and regulatory compliance requirements.',
        fullDescription:
            'Helps reinforce workplace health and safety, PPE use, and regulatory compliance requirements. Astra supports exposure control plans, safe work procedures, hazard prevention strategies, and PPE guidance to help protect staff, residents, and visitors.',
        keywords: ['health', 'safety', 'ppe', 'protective equipment'],
    },
    {
        id: 'construction',
        name: 'Atlas',
        label: 'Construction',
        agentType: 'CONSTRUCTION',
        image: constructionImg,
        imagePath: 'patty/guardian/construction.svg',
        description:
            'I can assist with any questions you have about Construction-related IPAC requirements.',
        shortDescription:
            'Specializes in infection control and compliance considerations for construction, renovation, and maintenance activities.',
        fullDescription:
            'Specializes in infection control and compliance considerations for construction, renovation, and maintenance activities. Atlas provides guidance on containment measures, dust and debris control, air quality considerations, and risk-reduction protocols throughout construction and ICRA-related projects.',
        keywords: ['construction', 'renovation', 'building', 'dust'],
    },
];

/**
 * Get guardian by ID
 * @param {string} id - Guardian ID
 * @returns {Object|undefined} Guardian object or undefined if not found
 */
export const getGuardianById = (id) => {
    if (!id) return undefined;
    return GUARDIANS.find((guardian) => guardian.id === id);
};

/**
 * Get guardian by agent type
 * @param {string} agentType - Agent type (e.g., 'HAND_HYGIENE')
 * @returns {Object|undefined} Guardian object or undefined if not found
 */
export const getGuardianByAgentType = (agentType) => {
    if (!agentType) return undefined;
    return GUARDIANS.find((guardian) => guardian.agentType === agentType);
};

/**
 * Find suggested guardian based on query keywords
 * @param {string} queryText - Query text to match against keywords
 * @param {string} currentAgentType - Current guardian's agent type to exclude
 * @returns {Object} Suggested guardian or Patty as fallback
 */
export const findSuggestedGuardian = (queryText, currentAgentType = null) => {
    // Handle undefined/null queryText
    if (!queryText || typeof queryText !== 'string') {
        return {
            id: 'all-topics',
            name: 'Patty',
            label: 'All Topics',
            agentType: 'PATTY',
        };
    }

    const query = queryText.toLowerCase();

    // Find guardian that matches keywords and is NOT the current guardian
    const matchedGuardian = GUARDIANS.find(
        (g) =>
            g.agentType !== currentAgentType &&
            g.keywords &&
            Array.isArray(g.keywords) &&
            g.keywords.some((keyword) => keyword && query.includes(keyword))
    );

    // If found a match, return it. Otherwise return Patty (All Topics) as fallback
    return (
        matchedGuardian || {
            id: 'all-topics',
            name: 'Patty',
            label: 'All Topics',
            agentType: 'PATTY',
        }
    );
};
